import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

type Pedido = {
  id: number;
  cidade: string;
  modalidade: string;
  prioridade: string;
  descricao?: string;
  pet_nome?: string | null;
  tipo_pet?: string | null;
  data_preferida?: string | null;
  data_hora_proposta?: string | null;
  valor_proposto?: number | string | null;
  mensagem_veterinario?: string | null;
  status: string;
  created_at: string;
};

function requestHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-CSRF-TOKEN": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
  };
}

export default function VetQuickRequests() {
  const isProvider = useLocation().pathname === "/vet/pedidos-recebidos";
  const endpoint = isProvider ? "/vet/solicitacoes" : "/tutor/solicitacoes-veterinarias";
  const [requests, setRequests] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<number | null>(null);
  const [offerTimes, setOfferTimes] = useState<Record<number, string>>({});
  const [offerPrices, setOfferPrices] = useState<Record<number, string>>({});
  const [messages, setMessages] = useState<Record<number, string>>({});

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(endpoint, { credentials: "same-origin", headers: { Accept: "application/json" } });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || `Erro HTTP ${res.status}`);
      setRequests(Array.isArray(payload.items) ? payload.items : []);
    } catch (err: any) { setError(err.message || "Não foi possível consultar pedidos."); }
    finally { setLoading(false); }
  };

  useEffect(() => { void refresh(); }, [endpoint]);

  const responder = async (item: Pedido, acao: "aceitar" | "recusar") => {
    if (busy !== null) return;
    setBusy(item.id);
    setError("");
    try {
      const data = offerTimes[item.id];
      const value = offerPrices[item.id];
      if (acao === "aceitar" && (!data || value === undefined || value.trim() === "")) {
        throw new Error("Preencha data, horário e valor da proposta.");
      }
      const res = await fetch(`/vet/solicitacoes/${item.id}/responder`, {
        method: "POST", credentials: "same-origin", headers: requestHeaders(),
        body: JSON.stringify({ acao, data_hora_proposta: acao === "aceitar" ? data : null,
          valor_proposto: acao === "aceitar" ? Number(value) : null,
          mensagem_veterinario: messages[item.id] || null }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || Object.values(payload.errors || {}).flat().join(" ") || `Erro ${res.status}`);
      await refresh();
    } catch (err: any) { setError(err.message || "Não foi possível responder."); }
    finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header activeTab="tutores" isCompact />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div><h1 className="text-2xl font-bold">{isProvider ? "Pedidos de atendimento recebidos" : "Meus pedidos veterinários"}</h1>
            <p className="text-sm text-muted-foreground">{isProvider ? "Responda com um horário e valor para avaliação do tutor." : "Acompanhe as propostas dos veterinários. Proposta não é consulta confirmada."}</p></div>
          <Link to="/" className="text-sm font-semibold text-forest underline">Voltar à AllePets</Link>
        </div>
        {error && <p role="alert" className="mb-4 rounded-lg border border-red-200 p-3 text-sm text-red-700">{error}</p>}
        {loading ? <p>Carregando pedidos…</p> : requests.length === 0 ? <p className="rounded-xl border p-5">Nenhum pedido encontrado.</p> : (
          <div className="space-y-4">
            {requests.map((item) => <article key={item.id} className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2"><strong>Pedido #{item.id}</strong><span className="rounded-full bg-slate-100 px-3 py-1 text-sm">{item.status === "proposta" ? "Proposta recebida" : item.status === "recusado" ? "Recusado" : "Aguardando"}</span></div>
              <p className="mt-2 text-sm text-slate-600">{item.cidade} · {item.modalidade} · {item.prioridade}{item.data_preferida ? ` · ${item.data_preferida}` : ""}</p>
              {isProvider && item.pet_nome && <p className="mt-2 text-sm font-semibold">Pet: {item.pet_nome}{item.tipo_pet ? ` (${item.tipo_pet})` : ""}</p>}
              {item.descricao && <p className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm">{item.descricao}</p>}
              {item.status === "proposta" && <div className="mt-3 rounded-lg border p-3 text-sm">
                <p><strong>Horário proposto:</strong> {item.data_hora_proposta ? new Date(item.data_hora_proposta.replace(" ", "T")).toLocaleString("pt-BR") : "A definir"}</p>
                <p><strong>Valor:</strong> {item.valor_proposto != null ? `R$ ${Number(item.valor_proposto).toFixed(2)}` : "A combinar"}</p>
                {item.mensagem_veterinario && <p className="mt-1">{item.mensagem_veterinario}</p>}
                {!isProvider && <p className="mt-2 text-xs text-muted-foreground">A reserva e o pagamento ainda não foram confirmados. Não compareça sem confirmar diretamente o atendimento.</p>}
              </div>}
              {isProvider && item.status === "pendente" && <div className="mt-4 space-y-3">
                <label className="block text-sm font-semibold">Data e horário disponíveis
                  <input type="datetime-local" value={offerTimes[item.id] || ""} onChange={(e) => setOfferTimes((v) => ({ ...v, [item.id]: e.target.value }))} className="mt-1 block w-full rounded-lg border p-2 font-normal" />
                </label>
                <label className="block text-sm font-semibold">Valor (R$)
                  <input type="number" min="0" step="0.01" value={offerPrices[item.id] || ""} onChange={(e) => setOfferPrices((v) => ({ ...v, [item.id]: e.target.value }))} className="mt-1 block w-full rounded-lg border p-2 font-normal" />
                </label>
                <label className="block text-sm font-semibold">Mensagem ao tutor (opcional)
                  <textarea maxLength={1200} rows={2} value={messages[item.id] || ""} onChange={(e) => setMessages((v) => ({ ...v, [item.id]: e.target.value }))} className="mt-1 block w-full rounded-lg border p-2 font-normal" />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={busy !== null} onClick={() => responder(item, "aceitar")} className="rounded-lg bg-forest px-4 py-2 font-semibold text-white disabled:opacity-50">Enviar proposta</button>
                  <button type="button" disabled={busy !== null} onClick={() => responder(item, "recusar")} className="rounded-lg border px-4 py-2 font-semibold disabled:opacity-50">Recusar pedido</button>
                </div>
              </div>}
            </article>)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
